import React, { useEffect, useState } from "react";
import axios from "axios";
import { server } from "../../server";
import { toast } from "react-toastify";
import { useCurrency } from "../../context/CurrencyContext";
import styles from "../../styles/styles";
import Loader from "../Layout/Loader";
import {
  FiSearch,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiInfo,
  FiDollarSign,
  FiAlertCircle,
} from "react-icons/fi";
import { SiPaypal } from "react-icons/si";
import { AiOutlineBank } from "react-icons/ai";
import { MdOutlinePayments } from "react-icons/md";
import { RxCross1 } from "react-icons/rx";
import { useLocation } from "react-router-dom";

const AllWithdraw = () => {
  const [data, setData] = useState([]);
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAnimating, setModalAnimating] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isRejecting, setIsRejecting] = useState(false);
  const { formatPrice } = useCurrency();

  const fetchWithdrawals = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${server}/withdraw/get-all-withdraw-request`,
        { withCredentials: true }
      );
      if (response.data.success) {
        setData(response.data.withdraws || []);
        
        // Auto-open modal if redirected from notification
        if (location.state?.relatedId && response.data.withdraws) {
          const target = response.data.withdraws.find(w => w._id === location.state.relatedId);
          if (target) {
            setSelectedRequest(target);
            setModalOpen(true);
            setTimeout(() => setModalAnimating(true), 10);
            
            // Clear the state so it doesn't auto-open again if user reloads the page
            window.history.replaceState({}, document.title);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching withdrawals:", error);
      toast.error(
        error.response?.data?.message || "Failed to load withdrawal requests"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWithdrawals();
  }, [location.state]);

  const openDetailsModal = (request) => {
    setSelectedRequest(request);
    setModalOpen(true);
    setTimeout(() => setModalAnimating(true), 10);
  };

  const closeDetailsModal = () => {
    setModalAnimating(false);
    setTimeout(() => {
      setModalOpen(false);
      setSelectedRequest(null);
      setIsRejecting(false);
      setRejectionReason("");
    }, 300);
  };

  // 1. PhonePe Payout
  const handlePhonePePayout = async (request, method) => {
    try {
      setActionLoading(true);
      const response = await axios.put(
        `${server}/withdraw/approve-withdrawal-with-phonepe-payout/${request._id}`,
        {
          sellerId: request.seller._id || request.seller,
          payoutMethod: method, // 'bank' or 'upi'
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success(response.data.message || "PhonePe Payout initiated successfully!");
        closeDetailsModal();
        fetchWithdrawals();
      }
    } catch (error) {
      console.error("PhonePe Payout error:", error);
      toast.error(error.response?.data?.message || "PhonePe payout failed");
    } finally {
      setActionLoading(false);
    }
  };

  // 2. PayPal Payout
  const handlePayPalPayout = async (request) => {
    try {
      setActionLoading(true);
      const response = await axios.put(
        `${server}/withdraw/approve-withdrawal-with-paypal-payout/${request._id}`,
        {
          sellerId: request.seller._id || request.seller,
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success(response.data.message || "PayPal Payout completed successfully!");
        closeDetailsModal();
        fetchWithdrawals();
      }
    } catch (error) {
      console.error("PayPal Payout error:", error);
      toast.error(error.response?.data?.message || "PayPal payout failed");
    } finally {
      setActionLoading(false);
    }
  };

  // 3. Manual Update
  const handleManualPayout = async (request) => {
    try {
      setActionLoading(true);
      const response = await axios.put(
        `${server}/withdraw/update-withdraw-request/${request._id}`,
        {
          sellerId: request.seller._id || request.seller,
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("Withdraw request updated successfully!");
        closeDetailsModal();
        fetchWithdrawals();
      }
    } catch (error) {
      console.error("Manual Payout error:", error);
      toast.error(error.response?.data?.message || "Manual update failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectPayout = async (request) => {
    if (!rejectionReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }

    try {
      setActionLoading(true);
      const response = await axios.put(
        `${server}/withdraw/reject-withdraw-request/${request._id}`,
        {
          sellerId: request.seller._id || request.seller,
          rejectionReason,
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("Withdraw request rejected and amount refunded.");
        closeDetailsModal();
        fetchWithdrawals();
      }
    } catch (error) {
      console.error("Reject Payout error:", error);
      toast.error(error.response?.data?.message || "Rejection failed");
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "succeed":
      case "Paid":
      case "payout_completed":
        return "bg-green-100 text-green-800 border border-green-200";
      case "Processing":
      case "Pending":
      case "payout_initiated":
        return "bg-yellow-100 text-yellow-800 border border-yellow-200";
      case "failed":
      case "Rejected":
      case "payout_failed":
        return "bg-red-100 text-red-800 border border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border border-gray-200";
    }
  };

  const filteredData = data.filter((item) => {
    const shopName = item.seller?.name || "";
    const status = item.status || "";
    const amount = String(item.amount || "");
    return (
      shopName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      status.toLowerCase().includes(searchTerm.toLowerCase()) ||
      amount.includes(searchTerm)
    );
  });

  const totalRequests = data.length;
  const pendingRequests = data.filter((d) => d.status === "Processing" || d.status === "payout_initiated").length;
  const completedRequests = data.filter((d) => d.status === "succeed" || d.status === "payout_completed").length;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader />
      </div>
    );
  }

  return (
    <div className="w-full p-4 800px:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl 800px:text-3xl font-bold text-gray-900 mb-2">
          Withdrawal Requests
        </h1>
        <p className="text-gray-600">
          Review and process payout requests submitted by vendors
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className={`${styles.card} p-5 flex items-center justify-between`}>
          <div>
            <p className="text-sm font-medium text-gray-600">Total Requests</p>
            <p className="text-2xl font-bold text-gray-900">{totalRequests}</p>
          </div>
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
            <FiInfo size={24} />
          </div>
        </div>

        <div className={`${styles.card} p-5 flex items-center justify-between`}>
          <div>
            <p className="text-sm font-medium text-gray-600">Pending Payouts</p>
            <p className="text-2xl font-bold text-orange-600">
              {pendingRequests}
            </p>
          </div>
          <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600">
            <FiClock size={24} />
          </div>
        </div>

        <div className={`${styles.card} p-5 flex items-center justify-between`}>
          <div>
            <p className="text-sm font-medium text-gray-600">Completed Payouts</p>
            <p className="text-2xl font-bold text-green-600">
              {completedRequests}
            </p>
          </div>
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center text-green-600">
            <FiCheckCircle size={24} />
          </div>
        </div>
      </div>

      {/* Filter and Table container */}
      <div className={`${styles.card} overflow-hidden p-6`}>
        {/* Search */}
        <div className="relative mb-6">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search by shop name, status, or amount..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.input}
            style={{ paddingLeft: "2.5rem" }}
          />
        </div>

        {/* Requests Table */}
        <div className="overflow-x-auto">
          {filteredData.length === 0 ? (
            <div className="text-center py-12">
              <FiDollarSign className="mx-auto h-12 w-12 text-gray-300" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No withdrawal requests found
              </h3>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Shop / Vendor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Requested Date
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredData.map((item) => (
                  <tr key={item._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="font-semibold text-gray-900">
                          {item.seller?.name || "Unknown Shop"}
                        </span>
                        <span className="text-xs text-gray-500">
                          {item.seller?.email || ""}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-bold text-gray-900">
                        {formatPrice(item.amount)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${getStatusColor(
                          item.status
                        )}`}
                      >
                        {item.status === "succeed" ? "Succeeded" : item.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(item.createdAt).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                      <button
                        onClick={() => openDetailsModal(item)}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-xs font-semibold shadow-md transition-colors"
                      >
                        Review Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {modalOpen && selectedRequest && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${
            modalAnimating ? "bg-black bg-opacity-50" : "bg-black bg-opacity-0"
          }`}
        >
          <div
            className={`bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden flex flex-col max-h-[90vh] transition-all duration-300 transform ${
              modalAnimating
                ? "opacity-100 scale-100 translate-y-0"
                : "opacity-0 scale-95 translate-y-4"
            }`}
          >
            {/* Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-primary-500 to-indigo-600 text-white">
              <h3 className="text-lg font-bold">Review Withdrawal Request</h3>
              <button
                onClick={closeDetailsModal}
                className="p-1 hover:bg-white hover:bg-opacity-20 rounded-full transition-all text-white"
              >
                <RxCross1 size={18} />
              </button>
            </div>

            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              {/* Vendor & Amount Info */}
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="text-xl font-bold text-gray-900">
                    {selectedRequest.seller?.name || "Unknown Shop"}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {selectedRequest.seller?.email || ""}
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-black text-primary-600">
                    {formatPrice(selectedRequest.amount)}
                  </span>
                  <p className="text-xs text-gray-400 mt-1">Withdrawal Amount</p>
                </div>
              </div>

              {/* Status details */}
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center space-x-3 text-sm">
                <FiInfo className="text-gray-400" size={18} />
                <span>
                  Current Status:{" "}
                  <strong className="capitalize">{selectedRequest.status}</strong>
                </span>
                {selectedRequest.payoutError && (
                  <div className="text-xs text-red-600 mt-1 flex items-center space-x-1">
                    <FiAlertCircle size={12} />
                    <span>Error: {selectedRequest.payoutError}</span>
                  </div>
                )}
              </div>

              {/* Payout Details Section */}
              <div className="border-t border-gray-150 pt-4">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                  Withdrawal Payment Accounts Set Up
                </h4>

                <div className="space-y-3">
                  {/* Bank Details */}
                  {selectedRequest.seller?.withdrawMethod ? (
                    <div className="p-3 border border-gray-200 bg-gray-50 rounded-lg flex items-start space-x-3">
                      <AiOutlineBank className="text-gray-600 mt-0.5" size={20} />
                      <div className="text-xs text-gray-700 grid grid-cols-2 gap-x-4 gap-y-1">
                        <div className="col-span-2 font-bold mb-1 text-gray-900">
                          Bank Details
                        </div>
                        <div>
                          <span className="text-gray-400 block">Bank Name</span>
                          <strong>{selectedRequest.seller.withdrawMethod.bankName}</strong>
                        </div>
                        <div>
                          <span className="text-gray-400 block">Holder Name</span>
                          <strong>{selectedRequest.seller.withdrawMethod.bankHolderName}</strong>
                        </div>
                        <div className="col-span-2">
                          <span className="text-gray-400 block">Account Number</span>
                          <strong className="font-mono">{selectedRequest.seller.withdrawMethod.bankAccountNumber}</strong>
                        </div>
                        <div>
                          <span className="text-gray-400 block">IFSC Code</span>
                          <strong className="font-mono">{selectedRequest.seller.withdrawMethod.ifscCode}</strong>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 italic">
                      No bank details configured by vendor.
                    </p>
                  )}
                </div>
              </div>

              {/* Actions Section */}
              <div className="border-t border-gray-150 pt-4 flex flex-col space-y-3">
                {selectedRequest.status === "Processing" ||
                selectedRequest.status === "Pending" ||
                selectedRequest.status === "payout_failed" ||
                selectedRequest.status === "failed" ? (
                  <>
                    <h4 className="text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Process Payout
                    </h4>

                    {isRejecting ? (
                      <div className="bg-red-50 p-4 rounded-lg border border-red-100 space-y-3">
                        <label className="block text-sm font-semibold text-red-800">
                          Reason for Rejection
                        </label>
                        <textarea
                          className="w-full border border-red-200 rounded p-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-500"
                          rows="3"
                          placeholder="Explain why this withdrawal is rejected. The amount will be refunded to their available balance."
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                        />
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => setIsRejecting(false)}
                            disabled={actionLoading}
                            className="px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded text-sm hover:bg-gray-50 transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleRejectPayout(selectedRequest)}
                            disabled={actionLoading || !rejectionReason.trim()}
                            className="px-3 py-1.5 bg-red-600 text-white rounded text-sm hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center space-x-1"
                          >
                            <FiXCircle size={14} />
                            <span>Confirm Reject & Refund</span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-3">
                        {/* Manual Payout */}
                        <button
                          onClick={() => handleManualPayout(selectedRequest)}
                          disabled={actionLoading}
                          className="px-4 py-2.5 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 disabled:opacity-50 flex items-center justify-center space-x-1"
                        >
                          <FiCheckCircle size={14} />
                          <span>Approve & Mark Paid</span>
                        </button>

                        {/* Reject Payout */}
                        <button
                          onClick={() => setIsRejecting(true)}
                          disabled={actionLoading}
                          className="px-4 py-2.5 bg-red-100 text-red-700 border border-red-200 rounded-lg text-xs font-semibold hover:bg-red-200 disabled:opacity-50 flex items-center justify-center space-x-1"
                        >
                          <FiXCircle size={14} />
                          <span>Reject Request</span>
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex justify-end pt-2">
                    <button
                      onClick={closeDetailsModal}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-semibold transition-colors"
                    >
                      Close Details
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllWithdraw;
